import React, { useMemo, useState } from 'react';
import { WidgetProps } from '@rjsf/utils';
import styled from '@emotion/styled';
import { Checkbox, Collapse, IconButton, List, ListItem, ListItemButton, Typography } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { TerritoryConstant } from '@/redux/slices/slice_types';
import {
  selectFlatTerritoryGroupingsWithRegion,
  selectGeoTerritoryGroupings,
  selectTimezoneTerritoryGroupings
} from '@/redux/selectors/constantsSelectors';
import { TIMEZONE_MAP } from '@/constants/constants';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TerritoryState, WindowTerritory } from '@/components/_common/SchemaForm/Widgets/TerritoriesWidget/TerritoriesWidget';
import { utcToZonedTime } from 'date-fns-tz';
import { formatDate, formatTime } from '@/_utils/date';

type TerritoryListProps = {
  registry: WidgetProps['registry'];
  selectedTerritories: TerritoryState[];
  onSelectedTerritoriesUpdate: (territories: TerritoryState[]) => void;
  formData: any;
  valuesOnly?: boolean;
  handleDownloadChange?: (territory: string, checked: boolean) => void;
  handleDeleteSelectedTerritory?: (territory: string | string[]) => void;
  readOnly: boolean;
  schema: WidgetProps['schema'];
};

type Territory = TerritoryConstant & { region: string };
type GroupedTerritories = Record<string, Territory[]>;

export const TerritoriesList = ({
  registry,
  selectedTerritories,
  onSelectedTerritoriesUpdate,
  formData,
  valuesOnly,
  handleDownloadChange,
  handleDeleteSelectedTerritory,
  readOnly,
  schema
}: TerritoryListProps) => {
  const territories = useAppSelector(selectFlatTerritoryGroupingsWithRegion);
  const timezones = useAppSelector(selectTimezoneTerritoryGroupings);
  const geoRegions = useAppSelector(selectGeoTerritoryGroupings);
  const [expandedGroup, setExpandedGroup] = useState<string[]>([]);

  const mergedGroupedTerritories = useMemo(() => {
    let newGroupTerritories: GroupedTerritories = {};

    if (registry?.rootSchema?.$defs?.WindowTerritory?.anyOf?.length > 0) {
      if (territories?.length > 0) {
        registry.rootSchema.$defs.WindowTerritory.anyOf.forEach((wTerritory: WindowTerritory) => {
          const value = wTerritory.enum[0];
          const title = wTerritory.title;
          if (!value || !title) return;
          if (valuesOnly && !selectedTerritories.find((st) => st.territory === value)) return;

          const territoryFromConstant = territories.find((t) => t.value.toLowerCase() === value.toLowerCase());
          const region = territoryFromConstant?.region || 'Others';
          if (Object.keys(newGroupTerritories).length === 0) {
            newGroupTerritories = { [region]: [{ label: title, value, region }] };
          } else {
            if (!newGroupTerritories[region]) {
              newGroupTerritories[region] = [{ label: title, value, region }];
            } else {
              newGroupTerritories[region].push({ label: title, value, region });
            }
          }
        });
      } else {
        newGroupTerritories = {
          Others: registry.rootSchema.$defs.WindowTerritory.anyOf.map((wTerritory: WindowTerritory) => ({
            label: wTerritory.title,
            value: wTerritory.enum[0],
            region: 'Others'
          }))
        } as GroupedTerritories;
      }
    }

    return newGroupTerritories;
  }, [registry, territories]);

  const handleGroupExpand = (newGroup: string) => {
    if (expandedGroup.length > 0 && expandedGroup.includes(newGroup)) {
      setExpandedGroup((prev) => prev.filter((p) => p !== newGroup));
      return;
    }

    setExpandedGroup((prev) => [...prev, newGroup]);
  };

  const handleGroupSelectionChange = (newValue: boolean, territoryGroup: Territory[]) => {
    if (handleDeleteSelectedTerritory) {
      handleDeleteSelectedTerritory(territoryGroup.map((tg) => tg.value));
      return;
    }
    // select all
    if (newValue) {
      if (selectedTerritories.length === 0) {
        onSelectedTerritoriesUpdate(territoryGroup.map((tg) => ({ territory: tg.value, download_availability: false })));
        return;
      }

      const notSelected = territoryGroup
        .filter((tg) => !selectedTerritories.some((st) => tg.value === st.territory))
        .map((ns) => ({ territory: ns.value, download_availability: false }));
      onSelectedTerritoriesUpdate([...selectedTerritories, ...notSelected]);
      return;
    }

    // deselect all
    const territoriesToRemove = new Set(territoryGroup.map((tg) => tg.value));
    const newSelectedTerritories = selectedTerritories.filter((st) => !territoriesToRemove.has(st.territory));
    onSelectedTerritoriesUpdate(newSelectedTerritories);
  };

  const handleGroupDownloadChange = (newValue: boolean, territoriesArray: string[]) => {
    if (handleDownloadChange) {
      territoriesArray.forEach((t) => handleDownloadChange(t, newValue));
      return;
    }
    // select all
    if (newValue) {
      onSelectedTerritoriesUpdate(
        selectedTerritories.map((st) => (territoriesArray.includes(st.territory) ? { ...st, download_availability: true } : st))
      );
      return;
    }

    // deselect all
    onSelectedTerritoriesUpdate(
      selectedTerritories.map((st) => (territoriesArray.includes(st.territory) ? { ...st, download_availability: false } : st))
    );
  };

  const handleTerritoryClick = (territoryCode: string) => {
    if (handleDeleteSelectedTerritory) return handleDeleteSelectedTerritory(territoryCode);
    let newSelectedTerritories = [];
    const newTerritory = { territory: territoryCode, download_availability: false };

    if (selectedTerritories.find((st) => st.territory === territoryCode)) {
      newSelectedTerritories = selectedTerritories.filter((st) => st.territory !== territoryCode);
    } else {
      newSelectedTerritories = [...selectedTerritories, newTerritory];
    }

    onSelectedTerritoriesUpdate(newSelectedTerritories);
  };

  const handleDownloadClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, territoryCode: string) => {
    e.preventDefault();
    e.stopPropagation();
    if (handleDownloadChange)
      return handleDownloadChange(territoryCode, !selectedTerritories.find((st) => st.territory === territoryCode)?.download_availability);
    const newSelectedTerritories = selectedTerritories.map((st) => {
      if (st.territory === territoryCode) {
        return { ...st, download_availability: !st.download_availability };
      }

      return st;
    });

    onSelectedTerritoriesUpdate(newSelectedTerritories);
  };

  const convertDateTimeToTimezone = (dateTimeStr: string, timezone: string) => {
    if (!dateTimeStr || !timezone) return '-';
    const fullTimeZone = TIMEZONE_MAP[timezone.toUpperCase()];
    if (!fullTimeZone) return '-';
    const zonedDate = utcToZonedTime(dateTimeStr, fullTimeZone);
    return formatDate(zonedDate) + ' ' + formatTime(zonedDate);
  };

  const TerritoryListItem = ({
    isDownloadChecked,
    selectedTerritory,
    territory,
    isIndented = false
  }: {
    isDownloadChecked: boolean;
    selectedTerritory: TerritoryState | undefined;
    territory: Territory;
    isIndented?: boolean;
  }) => {
    const timezone = timezones[territory.label] || '-';
    const regions = geoRegions[territory.label] || [];
    const startTime = formData?.windows?.start_date || schema?.properties?.windows?.default?.start_date;
    const endTime = formData?.windows?.end_date || schema?.properties?.windows?.default?.end_date;
    return (
      <StyledListItem disablePadding>
        <ListItemButton
          disableGutters
          dense
          disableRipple
          disabled={readOnly}
          sx={isIndented ? { pl: 6 } : { px: 1 }}
          onClick={() => !valuesOnly && handleTerritoryClick(territory.value)}
        >
          <ListItemDetailContainer isIndented={+isIndented}>
            {valuesOnly ? (
              <>{readOnly ? <div /> : <StyledDelete icon="delete" onClick={() => handleTerritoryClick(territory.value)} />}</>
            ) : (
              <Checkbox checked={!!selectedTerritory} />
            )}
            <div>{territory.label}</div>
            <div>{regions.length ? regions.map((r) => r.label).join(', ') : '-'}</div>
            <div>{timezone.toUpperCase()}</div>
            <div>{startTime ? convertDateTimeToTimezone(startTime, timezone) : '-'}</div>
            <div>{endTime ? convertDateTimeToTimezone(endTime, timezone) : '-'}</div>
            <Checkbox
              checked={isDownloadChecked}
              disabled={!selectedTerritory || readOnly}
              onClick={(e) => handleDownloadClick(e, territory.value)}
            />
          </ListItemDetailContainer>
        </ListItemButton>
      </StyledListItem>
    );
  };

  return (
    <>
      <ListItemHeader>
        {/* {valuesOnly ? (
          <StyledDelete icon="delete" onClick={() => handleSelectAllTerritoriesChange(false)} />
        ) : (
          <Checkbox
            checked={isSelectedAllChecked}
            indeterminate={isSelectedAllIndeterminate}
            onChange={(_, checked) => handleSelectAllTerritoriesChange(checked)}
          />
        )} */}
        <div />
        <Typography variant="caption">Territory</Typography>
        <Typography variant="caption">Geo Region</Typography>
        <Typography variant="caption">TZ</Typography>
        <Typography variant="caption">Start</Typography>
        <Typography variant="caption">End</Typography>
        <Typography variant="caption">Download Availability</Typography>
      </ListItemHeader>
      <StyledListGroup>
        {Object.keys(mergedGroupedTerritories).map((groupKey) => {
          if (!groupKey || groupKey === 'Others') return null;
          const territoryGroup = mergedGroupedTerritories[groupKey];
          const territoryValuesArray = territoryGroup.map((territory) => territory.value);
          const isExpanded = expandedGroup.includes(groupKey);
          const isGroupChecked = territoryValuesArray.every((tValue) =>
            selectedTerritories.find((selectedTerritory) => selectedTerritory.territory === tValue)
          );
          const isGroupIndeterminate =
            !isGroupChecked &&
            territoryValuesArray.some((tValue) => selectedTerritories.find((selectedTerritory) => selectedTerritory.territory === tValue));
          const isDownloadAvailabilityChecked =
            isGroupChecked &&
            territoryValuesArray.every(
              (tValue) => selectedTerritories.find((selectedTerritory) => selectedTerritory.territory === tValue)?.download_availability
            );
          const isDownloadAvailabilityIndeterminate =
            !isDownloadAvailabilityChecked &&
            territoryValuesArray.some(
              (tValue) => selectedTerritories.find((selectedTerritory) => selectedTerritory.territory === tValue)?.download_availability
            );

          return (
            <div key={`territory-group-${groupKey}`}>
              <StyledListItem disablePadding>
                <ListItemButton disableGutters dense disableRipple sx={{ paddingX: 1 }}>
                  <ListItemDetailContainer>
                    {valuesOnly ? (
                      <>{readOnly ? <div /> : <StyledDelete icon="delete" onClick={() => handleGroupSelectionChange(false, territoryGroup)} />}</>
                    ) : (
                      <Checkbox
                        checked={isGroupChecked}
                        indeterminate={isGroupIndeterminate}
                        onChange={(_, newValue) => handleGroupSelectionChange(newValue, territoryGroup)}
                      />
                    )}
                    <StyledGroupName>
                      <IconButton color="primary" onClick={() => handleGroupExpand(groupKey)}>
                        <PolarisIcon icon={isExpanded ? 'down' : 'right'} />
                      </IconButton>
                      <div>{groupKey}</div>
                    </StyledGroupName>
                    <Checkbox
                      checked={isDownloadAvailabilityChecked}
                      indeterminate={isDownloadAvailabilityIndeterminate}
                      disabled={!isGroupChecked || readOnly}
                      onChange={(_, newValue) => handleGroupDownloadChange(newValue, territoryValuesArray)}
                    />
                  </ListItemDetailContainer>
                </ListItemButton>
              </StyledListItem>
              <Collapse in={isExpanded}>
                <StyledList disablePadding>
                  {territoryGroup.map((territory) => {
                    const selectedTerritory = selectedTerritories.find((st) => st.territory === territory.value);
                    const isDownloadChecked = !!(selectedTerritory && selectedTerritory?.download_availability);

                    return (
                      <TerritoryListItem
                        key={`territory-item-${territory.value}`}
                        territory={territory}
                        selectedTerritory={selectedTerritory}
                        isDownloadChecked={isDownloadChecked}
                        isIndented
                      />
                    );
                  })}
                </StyledList>
              </Collapse>
            </div>
          );
        })}

        {/* Render OTHERS without group */}
        {mergedGroupedTerritories?.Others?.map((territory) => {
          const selectedTerritory = selectedTerritories.find((st) => st.territory === territory.value);
          const isDownloadChecked = !!(selectedTerritory && selectedTerritory?.download_availability);

          return (
            <div key={`territory-item-${territory.value}`}>
              <TerritoryListItem territory={territory} selectedTerritory={selectedTerritory} isDownloadChecked={isDownloadChecked} />
            </div>
          );
        })}
      </StyledListGroup>
    </>
  );
};

export default TerritoriesList;

const StyledGroupName = styled.div`
  grid-column: span 5;
  display: flex;
  align-items: center;
`;

const StyledDelete = styled(PolarisIcon)`
  margin-left: 12px;
  cursor: pointer;
`;

const StyledListGroup = styled(List)(({ theme }) => ({
  maxHeight: '70dvh',
  overflow: 'auto',
  '& > div:not(:last-of-type)': {
    marginBottom: theme.spacing(0.5)
  }
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  borderRadius: 8,
  '& .MuiListItemSecondaryAction-root': {
    paddingRight: theme.spacing(3.5)
  }
}));

const StyledList = styled(List)(({ theme }) => ({
  '& li:first-of-type': {
    marginTop: theme.spacing(0.5)
  },
  '& li:not(:last-of-type)': {
    marginBottom: theme.spacing(0.5)
  }
}));

const ListItemDetailContainer = styled.div<{ isIndented?: number }>`
  display: grid;
  grid-template-columns: 40px ${(props) => (props.isIndented ? '214px' : '254px')} 150px 50px 100px 100px 40px;
  width: 100%;
  align-items: center;
`;

const ListItemHeader = styled.div<{ isIndented?: number }>`
  display: grid;
  grid-template-columns: 50px ${(props) => (props.isIndented ? '214px' : '254px')} 150px 50px 100px 100px 40px;
  width: 100%;
  align-items: center;
`;
